/**
 * SavedReports - the report builder's saved reports (docs/plans/crm-report-builder-search-collab-plan.md
 * A4.3 steps 4 to 6, A4.4, B4.5, B4.6, decisions R5 to R7; work package R-7). Three presentational
 * pieces; ReportBuilder.tsx owns every call and all state:
 *   - SavedReports (default): My reports (every report I own, private or shared) and Team reports
 *     (every shared report), each with its pinned reports first. Pins are per device, under
 *     crm.reportBuilder.pinned (decision R6 a; builderLogic's pin helpers). Every row is a real link
 *     to the report's deep link, /crm/reports?tab=custom&report=<id>, so Ctrl-click, middle-click and
 *     right-click open it in a new tab; a plain click opens it in place (the builder asks first when
 *     the open report has unsaved changes). Both lists stay mounted and the hidden one is `hidden`.
 *   - ReportActions: New report, Save, Save as, Share with the team / Stop sharing, Copy (Save a copy
 *     on a report I may not change), Export and Archive. What is offered follows builderLogic
 *     canEdit, which is display only: the server decides who may change what (plan B4.5).
 *   - SaveReportDialog: a name (1 to 80 characters), an optional description (500 at most) and who
 *     can see it, only me (the default, decision R5 a) or the team.
 */
import { useId, useState } from 'react';
import type { FormEvent, MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Archive, Copy, Download, FilePlus, Loader2, Lock, Pin, Save, SaveAll, Share2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import Modal from '../../ui/Modal';
import { SegmentedControl } from '../components/filters';
import { Alert, btnDangerGhost, btnGhost, btnPrimary, fieldCls } from '../components/ui';
import { relTime } from '../lib';
import { REPORT_DESCRIPTION_MAX, REPORT_NAME_MAX, isPinned, pinnedFirst, savedReportProblem } from './builderLogic';
import type { ReportVisibility, SavedReport, SavedReportScope, SavedReportSummary } from './types';

// ---------------------------------------------------------------------------------------------
// The deep link
// ---------------------------------------------------------------------------------------------

/** The Reports page, where the Custom tab lives (plan B4.6). */
export const REPORTS_PATH = '/crm/reports';

/** The Custom tab, with a saved report open when an id is given: ?tab=custom&report=<id>. */
export function customReportHref(reportId?: string | null): string {
  const base = `${REPORTS_PATH}?tab=custom`;
  return reportId ? `${base}&report=${encodeURIComponent(reportId)}` : base;
}

/** A left click with no modifier key: the one click a row handles in place. */
export const isPlainClick = (e: ReactMouseEvent): boolean =>
  e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;

// ---------------------------------------------------------------------------------------------
// The two lists
// ---------------------------------------------------------------------------------------------

export const SCOPE_WORDS: Readonly<Record<SavedReportScope, string>> = {
  mine: 'My reports',
  team: 'Team reports',
};

const EMPTY_WORDS: Readonly<Record<SavedReportScope, string>> = {
  mine: 'No saved reports yet. Build one and press Save.',
  team: 'Nothing is shared with the team yet.',
};

/** One list as the builder holds it. */
export interface SavedListState {
  status: 'loading' | 'ready' | 'error';
  reports: SavedReportSummary[];
  error: string | null;
}

export interface SavedReportsProps {
  scope: SavedReportScope;
  onScopeChange: (scope: SavedReportScope) => void;
  lists: Readonly<Record<SavedReportScope, SavedListState>>;
  /** The report on screen (highlighted), or null. */
  openId: string | null;
  /** Pinned report ids, this device. */
  pinned: readonly string[];
  onTogglePin: (id: string) => void;
  /** A plain click on a row: open it in place. */
  onOpen: (id: string) => void;
  onRetry: () => void;
  /** A subject key in the catalog's words ("Leads"), or null. */
  subjectLabel: (key: string | null) => string | null;
  /** The signed-in user, to say "You" rather than a name. */
  userId: string | null;
}

/** A tiny dot between the parts of a meta line (kept out of the text, so it is never read aloud). */
const Sep = () => <span aria-hidden="true" className="mx-1.5 inline-block h-1 w-1 rounded-full bg-current align-middle opacity-60" />;

function metaParts(r: SavedReportSummary, scope: SavedReportScope, subjectLabel: SavedReportsProps['subjectLabel'], userId: string | null): string[] {
  const parts: string[] = [];
  if (scope === 'team') parts.push(r.owner_user_id && r.owner_user_id === userId ? 'You' : r.owner_name || 'A teammate');
  const subject = subjectLabel(r.subject);
  if (subject) parts.push(subject);
  if (scope === 'mine') parts.push(r.visibility === 'team' ? 'Shared' : 'Private');
  if (r.updated_at) parts.push(relTime(r.updated_at));
  return parts;
}

function ListBody({
  scope,
  state,
  openId,
  pinned,
  onTogglePin,
  onOpen,
  onRetry,
  subjectLabel,
  userId,
}: { scope: SavedReportScope; state: SavedListState } & Omit<SavedReportsProps, 'scope' | 'onScopeChange' | 'lists'>) {
  if (state.status === 'error') {
    return (
      <Alert
        tone="error"
        size="sm"
        actions={(
          <button type="button" onClick={onRetry} className="font-medium underline focus:outline-none focus:ring-2 focus:ring-primary-500">
            Try again
          </button>
        )}
      >
        {state.error}
      </Alert>
    );
  }
  if (state.status === 'loading' && state.reports.length === 0) {
    return (
      <p className="flex items-center gap-2 px-1 py-2 text-sm text-gray-500 dark:text-gray-400">
        <Loader2 className="h-4 w-4 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
        Loading...
      </p>
    );
  }
  if (state.reports.length === 0) {
    return <p className="px-1 py-2 text-sm text-gray-500 dark:text-gray-400">{EMPTY_WORDS[scope]}</p>;
  }
  const ordered = pinnedFirst(state.reports, pinned);
  return (
    <ul className="-mx-1 max-h-72 space-y-0.5 overflow-y-auto lg:max-h-80" aria-label={SCOPE_WORDS[scope]}>
      {ordered.map((r) => {
        const active = r.id === openId;
        const pinnedNow = isPinned(pinned, r.id);
        const parts = metaParts(r, scope, subjectLabel, userId);
        return (
          <li
            key={r.id}
            data-rb-report={r.id}
            className={`flex items-center gap-1 rounded-lg transition-colors duration-150 ${
              active ? 'bg-primary-50 dark:bg-primary-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'
            }`}
          >
            <Link
              to={customReportHref(r.id)}
              onClick={(e) => {
                if (!isPlainClick(e)) return;
                e.preventDefault();
                onOpen(r.id);
              }}
              aria-current={active ? 'true' : undefined}
              title={r.description || r.name}
              className="flex min-h-[44px] min-w-0 flex-1 flex-col justify-center rounded-lg px-2 py-1.5 no-underline focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0"
            >
              <span
                className={`truncate text-sm font-medium ${
                  active ? 'text-primary-700 dark:text-primary-300' : 'text-gray-900 dark:text-gray-100'
                }`}
              >
                {r.name}
              </span>
              {parts.length > 0 && (
                <span className="truncate text-xs text-gray-500 dark:text-gray-400">
                  {parts.map((p, i) => (
                    <span key={`${i}-${p}`}>
                      {i > 0 && <Sep />}
                      {p}
                    </span>
                  ))}
                </span>
              )}
            </Link>
            <button
              type="button"
              onClick={() => onTogglePin(r.id)}
              aria-pressed={pinnedNow}
              aria-label={pinnedNow ? `Unpin ${r.name}` : `Pin ${r.name} to the top`}
              title={pinnedNow ? 'Unpin (this device)' : 'Pin to the top (this device)'}
              data-rb-pin={r.id}
              className={`inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:h-8 sm:w-8 ${
                pinnedNow
                  ? 'text-primary-600 hover:bg-primary-100 dark:text-primary-400 dark:hover:bg-primary-900/40'
                  : 'text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-500 dark:hover:bg-gray-700 dark:hover:text-gray-200'
              }`}
            >
              <Pin className={`h-4 w-4 ${pinnedNow ? 'fill-current' : ''}`} aria-hidden="true" />
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/** My reports and Team reports, pinned first. */
export default function SavedReports(props: SavedReportsProps) {
  const { scope, onScopeChange, lists } = props;
  const headingId = useId();
  const scopes: SavedReportScope[] = ['mine', 'team'];
  return (
    <section
      aria-labelledby={headingId}
      data-rb="saved"
      className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800"
    >
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h2 id={headingId} className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Saved reports
        </h2>
        <SegmentedControl
          label="Which saved reports"
          value={scope}
          onChange={(v) => onScopeChange(v === 'team' ? 'team' : 'mine')}
          options={scopes.map((s) => ({ value: s, label: SCOPE_WORDS[s] }))}
        />
      </div>
      {scopes.map((s) => (
        <div key={s} data-rb-list={s} className={s === scope ? undefined : 'hidden'}>
          <ListBody
            scope={s}
            state={lists[s]}
            openId={props.openId}
            pinned={props.pinned}
            onTogglePin={props.onTogglePin}
            onOpen={props.onOpen}
            onRetry={props.onRetry}
            subjectLabel={props.subjectLabel}
            userId={props.userId}
          />
        </div>
      ))}
    </section>
  );
}

// ---------------------------------------------------------------------------------------------
// The action bar
// ---------------------------------------------------------------------------------------------

/** The action in flight, if any: every button waits while one runs. */
export type ReportBusy = 'save' | 'saveAs' | 'share' | 'copy' | 'export' | 'archive' | null;

export interface ReportActionsProps {
  /** The saved report on screen, or null for a new one. */
  report: SavedReport | null;
  /** The open report has changes that are not saved. */
  dirty: boolean;
  /** builderLogic canEdit (display only): offer Save, Share and Archive. */
  canChange: boolean;
  /** The spec can run: Save, Save as and Export need that. */
  canRun: boolean;
  /** Why the spec cannot run, for the disabled buttons' tooltips. */
  cannotRunWhy: string | null;
  busy: ReportBusy;
  onNew: () => void;
  onSave: () => void;
  onSaveAs: () => void;
  onToggleShare: () => void;
  onCopy: () => void;
  onExport: () => void;
  onArchive: () => void;
}

function ActionButton({
  label,
  icon,
  onClick,
  cls,
  disabled,
  spinning,
  title,
}: {
  label: string;
  icon: ReactNode;
  onClick: () => void;
  cls: string;
  disabled?: boolean;
  spinning?: boolean;
  title?: string;
}) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={title} className={`${cls} disabled:cursor-not-allowed`}>
      {spinning ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : icon}
      {label}
    </button>
  );
}

/** New report, Save, Save as, Share, Copy, Export and Archive for the report on screen. */
export function ReportActions({
  report,
  dirty,
  canChange,
  canRun,
  cannotRunWhy,
  busy,
  onNew,
  onSave,
  onSaveAs,
  onToggleShare,
  onCopy,
  onExport,
  onArchive,
}: ReportActionsProps) {
  const waiting = busy !== null;
  const blockedWhy = canRun ? undefined : cannotRunWhy || 'This report cannot run as it is.';
  const offerSave = !report || canChange;
  // A shared report whose author's account is gone (owner_user_id is ON DELETE SET NULL) would be seen
  // by nobody once private, so it is not offered; Archive is the way to retire it.
  const offerShare = !!report && canChange && !(report.visibility === 'team' && !report.owner_user_id);
  const saveTitle = !canRun
    ? blockedWhy
    : report && !dirty
      ? 'Nothing to save: no changes since it was saved.'
      : undefined;
  const icon = (Icon: LucideIcon) => <Icon className="h-4 w-4" aria-hidden="true" />;
  return (
    <div className="flex flex-wrap items-center gap-2" data-rb="actions">
      {offerSave && (
        <ActionButton
          label="Save"
          icon={icon(Save)}
          onClick={onSave}
          cls={btnPrimary}
          disabled={waiting || !canRun || (!!report && !dirty)}
          spinning={busy === 'save'}
          title={saveTitle}
        />
      )}
      <ActionButton
        label="Save as"
        icon={icon(SaveAll)}
        onClick={onSaveAs}
        cls={btnGhost}
        disabled={waiting || !canRun}
        spinning={busy === 'saveAs'}
        title={blockedWhy ?? 'Save these settings as a new report'}
      />
      {report && offerShare && (
        <ActionButton
          label={report.visibility === 'team' ? 'Stop sharing' : 'Share with the team'}
          icon={report.visibility === 'team' ? icon(Lock) : icon(Share2)}
          onClick={onToggleShare}
          cls={btnGhost}
          disabled={waiting}
          spinning={busy === 'share'}
          title={report.visibility === 'team'
            ? 'Make it private again: only its author will see it'
            : 'Teammates can open it and save a copy; only its author or an admin can change it'}
        />
      )}
      {report && (
        <ActionButton
          label={canChange ? 'Copy' : 'Save a copy'}
          icon={icon(Copy)}
          onClick={onCopy}
          cls={btnGhost}
          disabled={waiting}
          spinning={busy === 'copy'}
          title="A private copy in My reports"
        />
      )}
      <ActionButton
        label="Export"
        icon={icon(Download)}
        onClick={onExport}
        cls={btnGhost}
        disabled={waiting || !canRun}
        spinning={busy === 'export'}
        title={blockedWhy ?? 'Download the numbers on screen as a CSV file (the export is recorded)'}
      />
      <ActionButton
        label="New report"
        icon={icon(FilePlus)}
        onClick={onNew}
        cls={btnGhost}
        disabled={waiting}
        title="Start a new report"
      />
      {report && canChange && (
        <ActionButton
          label="Archive"
          icon={icon(Archive)}
          onClick={onArchive}
          cls={btnDangerGhost}
          disabled={waiting}
          spinning={busy === 'archive'}
          title="Stop listing this report for you and the team"
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// The save dialog
// ---------------------------------------------------------------------------------------------

export interface SaveDialogValues {
  name: string;
  description: string;
  visibility: ReportVisibility;
}

export interface SaveReportDialogProps {
  title: string;
  initial: SaveDialogValues;
  busy: boolean;
  /** The server's sentence for a refused save. */
  error: string | null;
  onSubmit: (values: SaveDialogValues) => void;
  onClose: () => void;
}

const VISIBILITY_CHOICES: readonly { value: ReportVisibility; label: string; hint: string }[] = [
  { value: 'private', label: 'Only me', hint: 'It stays under My reports until you share it.' },
  {
    value: 'team',
    label: 'The team',
    hint: 'Teammates see it under Team reports and can save a copy; only you or an admin can change it.',
  },
];

/** Name it, describe it, choose who sees it. Mount it afresh for each save (a key). */
export function SaveReportDialog({ title, initial, busy, error, onSubmit, onClose }: SaveReportDialogProps) {
  const [name, setName] = useState(initial.name);
  const [description, setDescription] = useState(initial.description);
  const [visibility, setVisibility] = useState<ReportVisibility>(initial.visibility);
  const [tried, setTried] = useState(false);
  const nameId = useId();
  const descriptionId = useId();
  const radioName = useId();
  const problem = savedReportProblem({ name, description });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (problem || busy) return;
    onSubmit({ name: name.trim(), description: description.trim(), visibility });
  };

  return (
    <Modal isOpen onClose={busy ? () => {} : onClose} title={title} maxWidth="max-w-md">
      <form onSubmit={submit} className="space-y-4" data-rb="save-dialog" noValidate>
        <div>
          <label htmlFor={nameId} className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">
            Name
          </label>
          <input
            id={nameId}
            name="name"
            type="text"
            value={name}
            maxLength={REPORT_NAME_MAX}
            onChange={(e) => setName(e.target.value)}
            placeholder="Weekly calls by recruiter"
            autoFocus
            className={`${fieldCls} min-h-[44px] w-full sm:min-h-0`}
          />
        </div>
        <div>
          <label htmlFor={descriptionId} className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">
            Description <span className="font-normal text-gray-500 dark:text-gray-400">(optional)</span>
          </label>
          <textarea
            id={descriptionId}
            name="description"
            value={description}
            maxLength={REPORT_DESCRIPTION_MAX}
            rows={3}
            onChange={(e) => setDescription(e.target.value)}
            className={`${fieldCls} w-full resize-y`}
          />
        </div>
        <fieldset>
          <legend className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">Who can see it</legend>
          <div className="space-y-1">
            {VISIBILITY_CHOICES.map((c) => (
              <label
                key={c.value}
                className={`flex min-h-[44px] cursor-pointer items-start gap-2 rounded-lg border px-3 py-2 transition-colors duration-150 sm:min-h-0 ${
                  visibility === c.value
                    ? 'border-primary-600 bg-primary-50 dark:border-primary-500 dark:bg-primary-900/20'
                    : 'border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/40'
                }`}
              >
                <input
                  type="radio"
                  name={radioName}
                  value={c.value}
                  checked={visibility === c.value}
                  onChange={() => setVisibility(c.value)}
                  className="mt-0.5 h-4 w-4 text-primary-600 focus:ring-primary-500"
                />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">{c.label}</span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400">{c.hint}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
        {tried && problem && <Alert tone="warning" size="sm">{problem}</Alert>}
        {error && <Alert tone="error" size="sm">{error}</Alert>}
        <div className="flex items-center justify-end gap-2 border-t border-gray-100 pt-4 dark:border-gray-700">
          <button type="button" onClick={onClose} disabled={busy} className={`${btnGhost} disabled:cursor-not-allowed`}>
            Cancel
          </button>
          <button type="submit" disabled={busy} className={`${btnPrimary} disabled:cursor-not-allowed`}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            Save
          </button>
        </div>
      </form>
    </Modal>
  );
}
